import { useSuspenseQuery } from '@tanstack/react-query';
import type React from 'react';
import { Suspense } from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { getSiteProductDefinition } from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Button,
    Description,
    Dialog,
    DialogBody,
    DialogBrandedLayout,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    Heading,
    intlPluralizeString,
    TextInput,
    useDialogClose,
} from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { useSetAnalyticsScreen } from '../../navigation';
import {
    apiQuery,
    assertOrganizationWithBilling,
    flattenSiteStructure,
    getMonthlySitePrice,
    useCheckoutSite,
} from '../../state';
import { PaymentFailedAlert } from '../Billing';
import { ContentIcon } from '../Icons';
import { priceFormatter } from '../OrganizationBilling';

export function PublishSiteDialog(props: {
    organization: api.Organization;
    site: api.Site | null;
    onPublishSuccess: (site: api.Site | null) => void;
}): React.ReactElement | null {
    const { organization, site, onPublishSuccess } = props;

    if (!site) {
        return null;
    }

    return (
        <Dialog size={{ width: 600 }}>
            <DialogBrandedLayout aside={null}>
                {site.published ? (
                    <PublishedContent organization={organization} site={site} />
                ) : (
                    <PublishContent
                        organization={organization}
                        site={site}
                        onPublishSuccess={onPublishSuccess}
                    />
                )}
            </DialogBrandedLayout>
        </Dialog>
    );
}

function PublishContent(props: {
    organization: api.Organization;
    site: api.Site;
    onPublishSuccess: (site: api.Site | null) => void;
}) {
    const { organization, site, onPublishSuccess } = props;

    return (
        <>
            <DialogHeader>
                <div className="flex items-center gap-2">
                    <Heading>{site.title} is ready to go live!</Heading>
                </div>
            </DialogHeader>

            <DialogBody className="mt-4">
                <Suspense
                    fallback={
                        <div className="flex flex-col justify-center gap-4">
                            <div className="h-6 w-full rounded-md bg-secondary motion-safe:animate-pulse" />
                            <div className="h-12 w-full rounded-md bg-secondary motion-safe:animate-pulse" />
                        </div>
                    }
                >
                    <div className="flex flex-col gap-4">
                        <PublishDetails organization={organization} site={site} />
                    </div>
                </Suspense>
            </DialogBody>

            <PublishFooter
                organization={organization}
                site={site}
                onPublishSuccess={onPublishSuccess}
            />
        </>
    );
}

function PublishedContent(props: { organization: api.Organization; site: api.Site }) {
    const { site, organization } = props;
    const logShareEvent = (platform: 'twitter' | 'linkedin' | 'reddit') => {
        analytics.logEvent(
            analytics.createSiteEvent(
                { id: site.id, organization: organization.id },
                { type: 'site_share', triggerLocation: 'published-dialog', platform }
            )
        );
    };

    return (
        <>
            <DialogBody>
                <div className="flex flex-col gap-6">
                    {site.visibility === api.SiteVisibility.VisitorAuth ? (
                        <>
                            <div className="flex flex-col">
                                <Heading>Your site is now live!</Heading>
                                <Description>
                                    Your site is only accessible to authenticated visitors.
                                </Description>
                            </div>
                            <Button
                                className="self-start"
                                kind="primary"
                                label="Manage authenticated access"
                                leadingIcon={Icon.PublishStatusVisitorAuth}
                                href={{
                                    screen: {
                                        type: 'site',
                                        organization: organization.id,
                                        site: site.id,
                                        tab: { type: 'settings', section: 'audience' },
                                    },
                                }}
                                onPress={() => {
                                    analytics.logEvent(
                                        analytics.createSiteEvent(
                                            { id: site.id, organization: organization.id },
                                            {
                                                type: 'site_manage_authenticated_access',
                                                triggerLocation: 'published-dialog',
                                            }
                                        )
                                    );
                                }}
                            />
                        </>
                    ) : null}
                    {site.visibility === api.SiteVisibility.ShareLink ? (
                        <>
                            <div className="flex flex-col">
                                <Heading>Your site is now live!</Heading>
                                <Description>
                                    You can now create share links to share your site with specific
                                    people.
                                </Description>
                            </div>
                            <Button
                                className="self-start"
                                kind="primary"
                                label="Manage share links"
                                leadingIcon={Icon.PublishStatusShareLink}
                                href={{
                                    screen: {
                                        type: 'site',
                                        organization: organization.id,
                                        site: site.id,
                                        tab: { type: 'settings', section: 'audience' },
                                    },
                                }}
                                onPress={() => {
                                    analytics.logEvent(
                                        analytics.createSiteEvent(
                                            { id: site.id, organization: organization.id },
                                            {
                                                type: 'site_manage_share_links',
                                                triggerLocation: 'published-dialog',
                                            }
                                        )
                                    );
                                }}
                            />
                        </>
                    ) : null}
                    {[api.SiteVisibility.Public, api.SiteVisibility.Unlisted].includes(
                        site.visibility
                    ) ? (
                        <>
                            <div className="flex flex-col">
                                <Heading>
                                    {site.urls.published
                                        ? 'Your site is now live!'
                                        : 'Your site will be live soon!'}
                                </Heading>
                                <Description>
                                    {site.urls.published
                                        ? "Let's share your new docs with the world."
                                        : 'Your site URL is being generated. It will be ready soon.'}
                                </Description>
                            </div>
                            {site.urls.published ? (
                                <>
                                    <TextInput
                                        leadingIcon={Icon.Link}
                                        value={site.urls.published || ''}
                                        readOnly
                                        copyable
                                        autoFocus
                                        onCopy={() => {
                                            analytics.logEvent(
                                                analytics.createSiteEvent(
                                                    {
                                                        id: site.id,
                                                        organization: organization.id,
                                                    },
                                                    {
                                                        type: 'site_copy_link',
                                                        triggerLocation: 'published-dialog',
                                                    }
                                                )
                                            );
                                        }}
                                    />
                                    <div className="flex flex-col gap-2">
                                        <div className="font-medium text-muted">Share online:</div>
                                        <div className="flex flex-wrap gap-4">
                                            <TweetButton
                                                url={site.urls.published}
                                                onPress={() => logShareEvent('twitter')}
                                            />
                                            <LinkedInButton
                                                url={site.urls.published}
                                                onPress={() => logShareEvent('linkedin')}
                                            />
                                            <RedditButton
                                                url={site.urls.published}
                                                onPress={() => logShareEvent('reddit')}
                                            />
                                        </div>
                                    </div>
                                </>
                            ) : null}
                        </>
                    ) : null}
                </div>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton label="Close" />
            </DialogFooter>
        </>
    );
}

// Publish details displayed in the publish dialog shown when a site can not be published immediately.
function PublishDetails(props: { organization: api.Organization; site: api.Site }) {
    const { organization, site } = props;
    const checkout = useCheckoutSite(organization, site);
    const { data: siteStructure } = useSuspenseQuery(
        apiQuery('getSiteStructure', { organizationId: organization.id, siteId: site.id })
    );
    const { data: organizationUsage } = useSuspenseQuery(
        apiQuery('getUsageForOrganizationById', { organizationId: organization.id })
    );

    const isAllDraftContent = (() => {
        const siteContent = flattenSiteStructure(siteStructure);

        return siteContent.length > 0 && siteContent.every((item) => item.draft);
    })();

    const productDefinition = getSiteProductDefinition(site.type);
    const checkoutResponse = checkout.state.type;

    useSetAnalyticsScreen(
        analytics.createSiteEvent(
            { id: site.id, organization: organization.id },
            {
                screen: 'site_publish_modal',
                publishType: checkoutResponse,
            }
        ),
        [site.id, organization.id, checkoutResponse]
    );

    // The user has permission to publish a site, so they should have access to billing information.
    assertOrganizationWithBilling(organization);
    const monthlyPrice = getMonthlySitePrice({
        siteType: productDefinition.key,
        billing: organization.billing,
    });

    const hasPaymentFailed = organization.billing?.hasPaymentFailed;

    return (
        <div className="flex flex-col gap-2">
            <Description>
                Your {productDefinition.title.toLowerCase()} site is ready to shine. Once published,
                you'll be able to share it with the world.
            </Description>

            <Description>
                {(() => {
                    switch (checkoutResponse) {
                        case 'trial-available':
                            return 'Your free trial will start automatically - no credit card required.';

                        case 'extended-trial-available':
                            return 'Restart your GitBook trial for another 7 days - no credit card required.';

                        case 'go-ahead':
                            return `Your site needs to be added to your subscription
                                before it can go live. Your subscription will increase by ${priceFormatter.format(monthlyPrice)} per month.`;

                        case 'checkout': {
                            const usageItems: string[] = [];

                            if (
                                'premium' in organizationUsage.sites &&
                                organizationUsage.sites.premium > 0
                            ) {
                                const premiumDefinition = getSiteProductDefinition(
                                    api.SiteType.Premium
                                );
                                usageItems.push(
                                    `${organizationUsage.sites.premium} ${intlPluralizeString({
                                        count: organizationUsage.sites.premium,
                                        singular: `${premiumDefinition.title} site`,
                                        plural: `${premiumDefinition.title} sites`,
                                    })}`
                                );
                            }

                            if (
                                'ultimate' in organizationUsage.sites &&
                                organizationUsage.sites.ultimate > 0
                            ) {
                                const ultimateDefinition = getSiteProductDefinition(
                                    api.SiteType.Ultimate
                                );
                                usageItems.push(
                                    `${organizationUsage.sites.ultimate} ${intlPluralizeString({
                                        count: organizationUsage.sites.ultimate,
                                        singular: `${ultimateDefinition.title} site`,
                                        plural: `${ultimateDefinition.title} sites`,
                                    })}`
                                );
                            }

                            usageItems.push(
                                `${organizationUsage.members.seats} ${intlPluralizeString({
                                    count: organizationUsage.members.seats,
                                    singular: 'Member',
                                    plural: 'Members',
                                })}`
                            );

                            return `
                                Publishing requires a subscription. 
                                You currently have ${usageItems.join(' and ')} included in your plan. 
                                Use the button below to customize your subscription.
                            `;
                        }
                        default:
                            assertNever(checkoutResponse);
                    }
                })()}
            </Description>

            {isAllDraftContent ? (
                <Alert
                    kind="info"
                    size="small"
                    title="All draft content will go live right away"
                    details="All your site content is currently draft. Publishing your site will automatically mark it as live content."
                />
            ) : null}

            {hasPaymentFailed ? (
                <Description className="pt-3">
                    <PaymentFailedAlert
                        organization={organization}
                        analyticsReason="publish_site_cta_payment_failure"
                    />
                </Description>
            ) : null}
        </div>
    );
}

function PublishFooter(props: {
    organization: api.Organization;
    site: api.Site;
    onPublishSuccess: (site: api.Site | null) => void;
}) {
    const { organization, site, onPublishSuccess } = props;
    const closeDialog = useDialogClose();
    const checkout = useCheckoutSite(organization, site, {
        onUpgradeSuccess: closeDialog,
    });

    return (
        <DialogFooter>
            <Button
                {...checkout.buttonProps}
                onPress={async () => {
                    const publishedSite = await checkout.onPress();
                    onPublishSuccess(publishedSite);
                }}
            />
        </DialogFooter>
    );
}

function TweetButton(props: { url: string; onPress?: () => void }) {
    const url = new URL('https://x.com/intent/tweet');
    url.searchParams.set('text', `Check out our new docs on @GitBookIO!\n${props.url}`);
    return (
        <Button
            kind="secondary"
            size="xlarge"
            iconOnly
            leadingIcon={<ContentIcon icon="x-twitter" />}
            label="Share on X"
            href={url.toString()}
            target="_blank"
            onPress={props.onPress}
        />
    );
}

function LinkedInButton(props: { url: string; onPress?: () => void }) {
    const url = new URL('https://www.linkedin.com/sharing/share-offsite/');
    url.searchParams.set('url', props.url);
    return (
        <Button
            kind="secondary"
            size="xlarge"
            iconOnly
            leadingIcon={<ContentIcon icon="linkedin" />}
            label="Share on LinkedIn"
            href={url.toString()}
            target="_blank"
            onPress={props.onPress}
        />
    );
}

function RedditButton(props: { url: string; onPress?: () => void }) {
    const url = new URL('https://reddit.com/submit');
    url.searchParams.set('url', props.url);
    url.searchParams.set('title', `Check out our new docs on GitBook!`);

    return (
        <Button
            kind="secondary"
            size="xlarge"
            iconOnly
            leadingIcon={<ContentIcon icon="reddit" />}
            label="Share on Reddit"
            href={url.toString()}
            target="_blank"
            onPress={props.onPress}
        />
    );
}
